import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { Widget } from "@typeform/embed-react";
import { getTimeZone, getTimeZoneOffset } from "../../utils/dateUtils";
import CareScreenerPasswordInput from "./CareScreenerPasswordInput";
import { useActions, useAtom } from "tiny-atom/react/hooks";
import AppointmentScheduler from "../appointments/AppointmentScheduler";
import BookedConsult from "../appointments/BookedConsult";
import { useGlobalContext } from "../../context/GlobalComponentsContext";
import "./CareScreener.css";
import { logEvent } from "../../actions";
import { reportError } from "../../utils/errorUtils";
import ReactGA from "react-ga4";
import { useBeforeunload } from "react-beforeunload";
import NoConsultNextSteps from "./NoConsultNextSteps";
import CareTeamFollowUp from "./CareTeamFollowUp";
import { accountCreationInterruption } from "./accountCreationRouting";
import CareScreenerService from "../../services/CareScreenerService";
import InsuranceEligibility from "./InsuranceEligibility";
import { LoadingTextVariant } from "./CareScreenerLoading";
import Flex from "../core/Flex";
import DirectIntakeRequested from "./DirectIntakeRequested";
import { User } from "../../model/userModels";
import { ScreenerView } from "../../model/careScreenerModels";
import CareScreenerProgress from "./CareScreenerProgress";
import IntakeConfirmation from "./IntakeConfirmation";
import ProviderSelection, { ProviderSelectionProps } from "./ProviderSelection";
import AccountCreationLoading from "./AccountCreationLoading";
import { CareScreenerContext } from "../../context/CareScreenerContext";
import { useSearchParams } from "react-router";
import AddInsuranceCard from "@components/billing/AddInsuranceCard";
import AddInsuranceCardBilling from "@components/billing/AddInsuranceCardBilling";
import CareScreenerDebugPanel from "./CareScreenerDebugPanel";
import TwoFactorAuthVerification from "./TwoFactorAuthVerification";
import CoverageResult from "./CoverageResult";
import { pollEligibilityResult } from "../../utils/eligibilityPolling";
import { CompleteCareScreenerResponse, EligibilityEstimate } from "../../services/CareScreenerService";
import {
  IS_DEV,
  IS_PROD,
  EVERNORTH_TRIGGER_PARAM,
  EVERNORTH_TRIGGER_VALUE,
  EVERNORTH_TYPEFORM_ID,
  PARENT_NAVIGATION_APPT_TYPE_ID,
  CONSULT_APPT_TYPE_ID
} from "../../utils/constants";

const qualifiesRef = { current: false };
const accountCreatedRef = { current: false };
const authenticationCompleteRef = { current: false };
const directIntakeQualifiedRef = { current: false };
const fullDirectIntakeQualifiedRef = { current: false };
const careCoordinatorRef: { current: User | undefined } = { current: undefined };
const stateRef = { current: "" };

type CareScreenerProps = {
  typeformId: string;
  typeformPath: "consult" | "lyra" | "pcscreener" | "newaccount" | "newaccountandconsult" | "evernorth";
  allowAppointmentScheduling?: boolean;
  insuranceEligiblityCheckEnabled?: boolean;
  accountCreationPredicate?: {
    tfQuestionRefShown: string;
    tfQuestionRefShownAlt?: string;
  };
};

const loadingTextVariants: LoadingTextVariant[] = [
  { text: "Finishing up your account..." },
  { text: "Checking our calendars..." },
  { text: "Choosing your Care Coordinator..." },
  { text: "Almost there, hang tight!" }
];

const savingResponsesTextVariants: LoadingTextVariant[] = [
  { text: "Saving your responses..." },
  { text: "Checking our calendars..." },
  { text: "Choosing your care coordinator..." },
  { text: "Finding the best fit for your family..." },
  { text: "Reviewing therapist availability..." },
  { text: "Tailoring your care plan..." },
  { text: "Setting up your family profile..." },
  { text: "Making sure everything looks great..." },
  { text: "Putting the finishing touches on..." },
  { text: "Almost there..." }
];

const verificationPendingTextVariants: LoadingTextVariant[] = [
  { text: "Connecting with your insurance provider..." },
  { text: "Looking up your plan details..." },
  { text: "Confirming your coverage..." },
  { text: "Verifying your deductible status..." },
  { text: "Checking your out-of-pocket estimates..." },
  { text: "Reviewing your benefits..." },
  { text: "Personalizing your experience..." },
  { text: "Preparing your dashboard..." },
  { text: "Getting everything ready for your first session..." },
  { text: "We want to make sure you get the best care..." },
  { text: "Hang tight, we're almost ready..." },
  { text: "Just confirming a few more details..." }
];

const FULL_DTI_ENABLED = true;

export default function CareScreener({
  typeformId,
  typeformPath,
  allowAppointmentScheduling,
  accountCreationPredicate,
  insuranceEligiblityCheckEnabled
}: CareScreenerProps) {
  const { setCandidates, insuranceEligibilityStatus } = useContext(CareScreenerContext)!;
  const {
    setConsultView,
    setCareScreenerSubmissionId,
    setTypeformResponseId
  }: {
    setConsultView: (view: ScreenerView) => void;
    setCareScreenerSubmissionId: (id: number) => void;
    setTypeformResponseId: (id: string) => void;
  } = useActions();
  const [scheduledAppointment, setScheduledAppointment] = useState();
  const [payerName, setPayerName] = useState("");
  const [childName, setChildName] = useState<string | undefined>(undefined);
  const [eligibilityEstimate, setEligibilityEstimate] = useState<EligibilityEstimate | undefined>(undefined);
  const { showError } = useGlobalContext();
  const consultView: ScreenerView = useAtom(state => state.consultView) || ScreenerView.TYPEFORM;
  const careScreenerSubmissionId = useAtom(state => state.careScreenerSubmissionId);
  const typeformResponseId = useAtom(state => state.typeformResponseId);
  const [searchParams] = useSearchParams();
  const [nextView, setNextView] = useState<ScreenerView | undefined>(undefined);
  const [schedulingQueryStartDate, setSchedulingQueryStartDate] = useState<string | null>(null);

  const enableFullDti = useMemo(
    () =>
      searchParams.get("fullDtiEnabled") == null ? FULL_DTI_ENABLED : searchParams.get("fullDtiEnabled") === "true",
    [searchParams]
  );

  const isEverNorth = useMemo(() => {
    const isEverNorthTypeform = typeformPath === "evernorth";
    const isEverNorthRoute = location.pathname === "/evernorth";
    const isEverNorthParams = searchParams.get(EVERNORTH_TRIGGER_PARAM)?.toLowerCase() === EVERNORTH_TRIGGER_VALUE;
    return isEverNorthTypeform || isEverNorthRoute || isEverNorthParams;
  }, [searchParams, typeformPath]);

  const showProgress = typeformPath === "consult";

  useEffect(() => {
    logEvent("careScreenerShown", { value: typeformPath });
  }, [typeformPath]);

  useBeforeunload(
    [
      ScreenerView.NEXT_STEPS,
      ScreenerView.CONSULT_BOOKED,
      ScreenerView.DIRECT_INTAKE_REQUESTED,
      ScreenerView.INTAKE_CONFIRMATION
    ].includes(consultView)
      ? null
      : event => event.preventDefault()
  );

  const isAccountCreationEnabled = useCallback(
    () => !accountCreationPredicate || qualifiesRef.current,
    [accountCreationPredicate]
  );

  const handleTypeformSubmitted = useCallback(
    async (payload: { responseId: string }) => {
      if (!payload?.responseId) return;
      logEvent("careScreenerSubmission", { value: typeformPath });
      try {
        setTypeformResponseId(payload.responseId);
        const result = await CareScreenerService.createCareScreenerSubmission({
          typeformId: typeformId,
          typeformPath: typeformPath,
          typeformResponseId: payload.responseId
        });
        if ((typeformPath === "consult" || typeformPath === "pcscreener") && IS_PROD) {
          ReactGA.event({ action: "typeform_submit", category: "marketing_funnel" });
        }
        setCareScreenerSubmissionId(result.id);

        if (!isAccountCreationEnabled()) {
          return;
        }

        setConsultView(ScreenerView.SAVING_RESPONSES);

        if (insuranceEligiblityCheckEnabled) {
          if (typeformId === EVERNORTH_TYPEFORM_ID) {
            setConsultView(ScreenerView.INSURANCE_ELIGIBILITY);
            return;
          }
          try {
            const discoveryResult = await CareScreenerService.runCareScreenerDiscovery(result.id);
            if (discoveryResult.skipInsurance) {
              setConsultView(ScreenerView.PASSWORD_INPUT);
            } else if (discoveryResult.found) {
              setPayerName(discoveryResult.payerName || "");
              setConsultView(ScreenerView.PASSWORD_INPUT);
            } else {
              setConsultView(ScreenerView.INSURANCE_CARD_UPLOAD);
            }
          } catch {
            setConsultView(ScreenerView.INSURANCE_CARD_UPLOAD);
          }
        } else {
          setConsultView(ScreenerView.PASSWORD_INPUT);
        }
      } catch (error) {
        reportError("handleTypeformSubmitted", error);
        showError("Something went wrong. Please contact support@getmanatee.com");
      }
    },
    [
      insuranceEligiblityCheckEnabled,
      isAccountCreationEnabled,
      setCareScreenerSubmissionId,
      setConsultView,
      setTypeformResponseId,
      showError,
      typeformId,
      typeformPath
    ]
  );

  useEffect(() => {
    if (consultView === ScreenerView.TYPEFORM_SUBMISSION && IS_DEV) {
      setConsultView(ScreenerView.LOADING_TYPEFORM_SUBMISSION);
      handleTypeformSubmitted({ responseId: typeformResponseId });
    }
  }, [consultView, handleTypeformSubmitted, typeformResponseId, setConsultView]);

  const computeScheduleView = () => {
    if (!allowAppointmentScheduling) return ScreenerView.NEXT_STEPS;
    if (directIntakeQualifiedRef.current && careCoordinatorRef.current) {
      if (enableFullDti && fullDirectIntakeQualifiedRef.current) return ScreenerView.PROVIDER_SELECTION;
      if (!enableFullDti) return ScreenerView.DIRECT_INTAKE_REQUESTED;
    }
    return ScreenerView.SCHEDULE_CONSULT;
  };

  const applyAccountCreationResult = (result: CompleteCareScreenerResponse) => {
    setSchedulingQueryStartDate(result.schedulingQueryStartDate ?? null);
    const firstChildName = result.candidates?.[0]?.user?.firstName;
    if (firstChildName) setChildName(firstChildName);
    if (setCandidates && result.directIntakeStatus === "SCHEDULABLE") {
      setCandidates(result.candidates);
      fullDirectIntakeQualifiedRef.current = true;
      stateRef.current = result.state;
      directIntakeQualifiedRef.current = true;
      careCoordinatorRef.current = result.careCoordinator;
      logEvent("fullDirectIntakeQualified", { id: typeformResponseId });
    }
    if (result.directIntakeStatus === "QUALIFIED" || result.directIntakeStatus === "INTAKE_REQUESTED") {
      directIntakeQualifiedRef.current = true;
      careCoordinatorRef.current = result.careCoordinator;
      logEvent("directIntakeQualified", { id: typeformResponseId });
    } else if (result.directIntakeStatus === "UNQUALIFIED") {
      logEvent("directIntakeUnqualified", { id: typeformResponseId });
    } else if (result.directIntakeStatus === "NOT_APPLICABLE") {
      logEvent("directIntakeNotApplicable", { id: typeformResponseId });
    }
    accountCreatedRef.current = true;
    authenticationCompleteRef.current = true;
  };

  const enterAccountCreationInterruption = (result: CompleteCareScreenerResponse): boolean => {
    const interruption = accountCreationInterruption(result);
    if (!interruption) return false;
    if (interruption === ScreenerView.TWO_FACTOR_AUTH) {
      sessionStorage.setItem("userId", result.userId.toString());
    } else {
      logEvent("careScreenerCareTeamFollowUp", { id: typeformResponseId });
    }
    setConsultView(interruption);
    return true;
  };

  const handlePasswordSubmitted = async (password: string | undefined) => {
    if (!password) return;
    try {
      if (payerName) {
        setConsultView(ScreenerView.VERIFICATION_PENDING);

        const accountCreationPromise = CareScreenerService.completeCareScreener({
          careScreenerSubmissionId,
          typeformId,
          typeformResponseId,
          password,
          timeZone: getTimeZone(),
          timeZoneOffset: getTimeZoneOffset(),
          enableFullDti
        }).catch(err => err as Error);

        const [eligibilityResult, accountResultOrError] = await Promise.all([
          pollEligibilityResult(careScreenerSubmissionId, CareScreenerService.getCareScreenerEligibilityResult),
          accountCreationPromise
        ]);

        if (accountResultOrError instanceof Error) {
          reportError(
            `handlePasswordSubmitted -- completeCareScreener failed -- typeformResponseId=${typeformResponseId} -- ${accountResultOrError}`
          );
          if (eligibilityResult.status === "ELIGIBLE") {
            setEligibilityEstimate(eligibilityResult.estimate);
            setNextView(ScreenerView.SCHEDULE_CONSULT);
            setConsultView(ScreenerView.COVERAGE_RESULT);
          } else {
            setConsultView(ScreenerView.PASSWORD_INPUT);
            showError("Something went wrong. Please try again or contact support@getmanatee.com.");
          }
          return;
        }

        const accountResult = accountResultOrError as CompleteCareScreenerResponse;

        if (enterAccountCreationInterruption(accountResult)) return;

        logEvent("careScreenerCompletion", { id: typeformResponseId, value: typeformPath });
        applyAccountCreationResult(accountResult);

        if (eligibilityResult.status === "ELIGIBLE") {
          setEligibilityEstimate(eligibilityResult.estimate);
          setNextView(computeScheduleView());
          setConsultView(ScreenerView.COVERAGE_RESULT);
        } else {
          setConsultView(ScreenerView.INSURANCE_CARD_UPLOAD);
        }
      } else {
        setConsultView(ScreenerView.LOADING_ACCOUNT_CREATION);

        const result = await CareScreenerService.completeCareScreener({
          careScreenerSubmissionId,
          typeformId,
          typeformResponseId,
          password,
          timeZone: getTimeZone(),
          timeZoneOffset: getTimeZoneOffset(),
          enableFullDti
        });

        if (enterAccountCreationInterruption(result)) return;

        logEvent("careScreenerCompletion", { id: typeformResponseId, value: typeformPath });
        applyAccountCreationResult(result);
      }
    } catch (error) {
      setConsultView(ScreenerView.PASSWORD_INPUT);
      reportError(`handlePasswordSubmitted -- typeformReponseId=${typeformResponseId} -- ${error}`);
      showError(error?.message);
    }
  };

  const handleQuestionChanges = useCallback(
    (event: { ref: string }) => {
      if (accountCreationPredicate) {
        if (
          event?.ref == accountCreationPredicate.tfQuestionRefShown ||
          (accountCreationPredicate.tfQuestionRefShownAlt &&
            event?.ref == accountCreationPredicate.tfQuestionRefShownAlt)
        ) {
          qualifiesRef.current = true;
        } else {
          qualifiesRef.current = false;
        }
      }
    },
    [accountCreationPredicate]
  );

  const handlePostAccountCardSubmit = useCallback(
    async (eligibilityStatus: string | undefined) => {
      if (eligibilityStatus === "ELIGIBLE") {
        try {
          const result = await CareScreenerService.getCareScreenerEligibilityResult(careScreenerSubmissionId);
          if (result.status === "ELIGIBLE") setEligibilityEstimate(result.estimate);
        } catch {
          // estimate is best-effort; still show coverage result
        }
        setNextView(computeScheduleView());
        setConsultView(ScreenerView.COVERAGE_RESULT);
      } else {
        setConsultView(computeScheduleView());
      }
    },
    // computeScheduleView reads only refs and stable props — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [careScreenerSubmissionId, setConsultView]
  );

  const handleInsuranceCardSubmit = useCallback(
    async (frontImage, backImage) => {
      const result = await CareScreenerService.submitInsuranceCard({
        id: careScreenerSubmissionId,
        frontImage,
        backImage
      });
      if (accountCreatedRef.current) {
        await handlePostAccountCardSubmit(result.eligibilityStatus);
      } else {
        setPayerName(result.payerName || "");
        setConsultView(ScreenerView.PASSWORD_INPUT);
      }
    },
    [careScreenerSubmissionId, handlePostAccountCardSubmit, setConsultView]
  );

  const handleInsuranceCardManualSubmit = useCallback(
    async (memberId: string, groupNumber: string) => {
      const result = await CareScreenerService.submitInsuranceCardManual({
        id: careScreenerSubmissionId,
        memberId,
        groupNumber
      });
      if (accountCreatedRef.current) {
        await handlePostAccountCardSubmit(result.eligibilityStatus);
      } else {
        setPayerName(result.payerName || "");
        setConsultView(ScreenerView.PASSWORD_INPUT);
      }
    },
    [careScreenerSubmissionId, handlePostAccountCardSubmit, setConsultView]
  );

  const handleConsultScheduled = useCallback(
    appointment => {
      logEvent("appointmentScheduled", { value: typeformPath });
      if (IS_PROD) {
        ReactGA.event({ action: "consult_booked", category: "marketing_funnel" });
      }
      if (directIntakeQualifiedRef.current) {
        logEvent("consultBookedDirectIntakeQualified", { value: typeformPath });
      }
      logEvent("consultBooked", { value: typeformPath });
      setScheduledAppointment(appointment);
      setConsultView(ScreenerView.CONSULT_BOOKED);
      setNextView(undefined);
    },
    [setConsultView, typeformPath]
  );

  const handleCreateAccountsLoadingTextRotation = useCallback(() => {
    if (accountCreatedRef.current && authenticationCompleteRef.current) {
      if (allowAppointmentScheduling) {
        if (directIntakeQualifiedRef.current && careCoordinatorRef.current) {
          if (enableFullDti) {
            if (fullDirectIntakeQualifiedRef.current) {
              setConsultView(ScreenerView.PROVIDER_SELECTION);
            } else {
              setConsultView(ScreenerView.SCHEDULE_CONSULT);
            }
          } else {
            setConsultView(ScreenerView.DIRECT_INTAKE_REQUESTED);
          }
        } else {
          setConsultView(ScreenerView.SCHEDULE_CONSULT);
        }
      } else {
        setConsultView(ScreenerView.NEXT_STEPS);
      }
    }
  }, [allowAppointmentScheduling, enableFullDti, setConsultView]);

  const Typeform = useCallback(() => {
    return (
      <Widget
        onSubmit={handleTypeformSubmitted}
        onQuestionChanged={handleQuestionChanges}
        style={{
          height: "90vh",
          width: window.innerWidth < 768 ? 350 : 600
        }}
        id={typeformId}
        transitiveSearchParams={true}
      />
    );
  }, [handleQuestionChanges, handleTypeformSubmitted, typeformId]);

  const progressComponent = useMemo(() => <CareScreenerProgress currentView={consultView} />, [consultView]);

  const postProviderSelectionProps = useMemo<
    Pick<ProviderSelectionProps, "continueButtonLabel" | "onContinueButtonPressed">
  >(() => {
    return {
      continueButtonLabel: "finish up",
      onContinueButtonPressed: () => {
        logEvent("fullDirectIntakeScheduled");
        setConsultView(ScreenerView.INTAKE_CONFIRMATION);
        setNextView(undefined);
      }
    };
  }, [setConsultView]);

  return (
    <div className="consult-container">
      {IS_DEV && (
        <CareScreenerDebugPanel
          currentView={consultView}
          onViewChange={setConsultView}
          onCareScreenerSubmissionIdChange={setCareScreenerSubmissionId}
          onTypeformResponseIdChange={setTypeformResponseId}
          typeformId={typeformId}
          typeformPath={typeformPath}
          currentCareScreenerSubmissionId={careScreenerSubmissionId}
          currentTypeformResponseId={typeformResponseId}
        />
      )}

      {consultView === ScreenerView.SAVING_RESPONSES && (
        <div className="column-centered">
          <AccountCreationLoading
            textVariants={savingResponsesTextVariants}
            onTextRotation={() => {}}
            progressComponent={showProgress ? progressComponent : undefined}
          />
        </div>
      )}
      {consultView === ScreenerView.LOADING_ACCOUNT_CREATION && (
        <div className="column-centered">
          <AccountCreationLoading
            textVariants={loadingTextVariants}
            onTextRotation={handleCreateAccountsLoadingTextRotation}
            progressComponent={showProgress ? progressComponent : undefined}
          />
        </div>
      )}
      {consultView === ScreenerView.TYPEFORM && (
        <div className="column-centered">
          <Typeform />
        </div>
      )}
      {consultView === ScreenerView.INSURANCE_ELIGIBILITY && !!(careScreenerSubmissionId && typeformResponseId) && (
        <div className="column-centered">
          <InsuranceEligibility
            careScreenerSubmission={{
              id: careScreenerSubmissionId,
              typeformId,
              typeformResponseId
            }}
            onContinue={() => {
              setConsultView(ScreenerView.PASSWORD_INPUT);
            }}
            progressComponent={progressComponent}
            onPasswordSubmitted={handlePasswordSubmitted}
            consultPath={typeformPath}
            skipCheck={!!insuranceEligibilityStatus}
          />
        </div>
      )}
      {consultView === ScreenerView.PASSWORD_INPUT && (
        <div className="column-centered">
          <CareScreenerPasswordInput
            onPasswordSubmitted={handlePasswordSubmitted}
            consultPath={typeformPath}
            progressComponent={showProgress ? progressComponent : undefined}
            headline={payerName ? `Great news! We found your ${payerName} plan.` : undefined}
          />
        </div>
      )}
      {consultView === ScreenerView.VERIFICATION_PENDING && (
        <div className="column-centered">
          <AccountCreationLoading
            textVariants={verificationPendingTextVariants}
            onTextRotation={() => {}}
            progressComponent={showProgress ? progressComponent : undefined}
          />
        </div>
      )}
      {consultView === ScreenerView.COVERAGE_RESULT && (
        <div className="column-centered">
          <CoverageResult
            childName={childName}
            estimate={eligibilityEstimate}
            onContinue={() => {
              setConsultView(nextView || ScreenerView.SCHEDULE_CONSULT);
              setNextView(undefined);
            }}
            progressComponent={showProgress ? progressComponent : undefined}
          />
        </div>
      )}
      {consultView === ScreenerView.TWO_FACTOR_AUTH && (
        <div className="column-centered">
          <TwoFactorAuthVerification
            progressComponent={progressComponent}
            onVerificationComplete={success => {
              if (success) {
                authenticationCompleteRef.current = true;
                setConsultView(ScreenerView.SCHEDULE_CONSULT);
              }
            }}
          />
        </div>
      )}
      {consultView === ScreenerView.SCHEDULE_CONSULT && (
        <AppointmentScheduler
          appointmentId={isEverNorth ? PARENT_NAVIGATION_APPT_TYPE_ID : CONSULT_APPT_TYPE_ID}
          appointmentUserId={undefined}
          appointmentContactMethod={"Phone Call"}
          availabilityRecurringInterval={undefined}
          availabilityRecurringCount={undefined}
          onAppointmentScheduled={handleConsultScheduled}
          schedulingQueryStartDate={schedulingQueryStartDate}
          careTeamMemberIds={careCoordinatorRef.current ? [careCoordinatorRef.current.id] : undefined}
          headerComponent={<Flex style={{ height: 20 }} />}
          style={{
            margin: "auto",
            marginTop: 30,
            marginBottom: 30,
            padding: 30
          }}
        />
      )}
      {consultView === ScreenerView.CONSULT_BOOKED && (
        <BookedConsult appointment={scheduledAppointment} typeformPath={typeformPath} />
      )}
      {consultView === ScreenerView.DIRECT_INTAKE_REQUESTED && !!careCoordinatorRef.current && (
        <DirectIntakeRequested
          careCoordinator={careCoordinatorRef.current}
          onBookConsultPressed={function (): void {
            logEvent("directIntakeBookConsultPressed");
            setConsultView(ScreenerView.SCHEDULE_CONSULT);
          }}
        />
      )}
      {consultView === ScreenerView.PROVIDER_SELECTION && (
        <ProviderSelection
          careCoordinator={careCoordinatorRef.current}
          progressComponent={showProgress ? progressComponent : undefined}
          schedulingQueryStartDate={schedulingQueryStartDate}
          state={stateRef.current}
          onBookConsultPressed={function (): void {
            logEvent("fullDirectIntakeBookConsultPressed");
            setConsultView(ScreenerView.SCHEDULE_CONSULT);
          }}
          {...postProviderSelectionProps}
        />
      )}
      {consultView === ScreenerView.NEXT_STEPS && <NoConsultNextSteps typeformPath={typeformPath} />}
      {consultView === ScreenerView.CARE_TEAM_FOLLOW_UP && <CareTeamFollowUp />}
      {consultView === ScreenerView.INTAKE_CONFIRMATION && (
        <IntakeConfirmation
          careCoordinator={careCoordinatorRef.current}
          progressComponent={showProgress ? progressComponent : undefined}
        />
      )}
      {consultView === ScreenerView.ADD_INSURANCE_CARD && (
        <AddInsuranceCardBilling
          headerComponent={progressComponent}
          logEventParams={{ source: "screener" }}
          required={false}
          onContinuePress={() => {
            const view = nextView || ScreenerView.INTAKE_CONFIRMATION;
            setConsultView(view);
            setNextView(undefined);
          }}
        />
      )}
      {consultView === ScreenerView.INSURANCE_CARD_UPLOAD && (
        <AddInsuranceCard
          onSubmitCard={handleInsuranceCardSubmit}
          onSubmitManual={handleInsuranceCardManualSubmit}
          headerComponent={progressComponent}
          title="We need a little help..."
          description="Add your insurance card or information so we can confirm your benefits"
        />
      )}
    </div>
  );
}
